import React from 'react';
import { Box, Stack, Typography, Paper, Chip } from '@mui/material';
import { HelpCircle, MessageSquare, BarChart2, Bot } from 'lucide-react';

export interface IQA {
  question: string;
  answer: string;
  score: number;
  justification: string;
}

export interface IQAProps {
  data: IQA[];
}

export const QA: React.FC<IQAProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ py: 8, px: 2 }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <HelpCircle size={40} style={{ color: '#9ca3af' }} />
        </Box>
        <Typography variant='h6' sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          No Q&A Available
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 380 }}>
          Interview questions and answers will appear here once they are recorded.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {data.map((qa, index) => {
        let scoreColor = '#6b7280';
        let scoreBg = 'linear-gradient(to bottom right, #6b7280, #4b5563)';
        let borderColor = '#e5e7eb';
        let headerBg = 'action.hover';

        if (qa.score >= 80) {
          scoreColor = '#16a34a';
          scoreBg = 'linear-gradient(to bottom right, #22c55e, #16a34a)';
          borderColor = 'rgba(34, 197, 94, 0.3)';
          headerBg = 'rgba(34, 197, 94, 0.05)';
        } else if (qa.score >= 60) {
          scoreColor = '#2563eb';
          scoreBg = 'linear-gradient(to bottom right, #3b82f6, #2563eb)';
          borderColor = 'rgba(59, 130, 246, 0.3)';
          headerBg = 'rgba(59, 130, 246, 0.05)';
        } else if (qa.score >= 40) {
          scoreColor = '#ea580c';
          scoreBg = 'linear-gradient(to bottom right, #f97316, #ea580c)';
          borderColor = 'rgba(249, 115, 22, 0.3)';
          headerBg = 'rgba(249, 115, 22, 0.05)';
        } else {
          scoreColor = '#dc2626';
          scoreBg = 'linear-gradient(to bottom right, #ef4444, #dc2626)';
          borderColor = 'rgba(239, 68, 68, 0.3)';
          headerBg = 'rgba(239, 68, 68, 0.05)';
        }

        return (
          <Paper
            key={index}
            variant='outlined'
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              borderColor,
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 2,
              },
            }}
          >
            {/* Header with Question Number and Score */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: headerBg,
                borderBottom: '1px solid',
                borderColor,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: scoreBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    boxShadow: 1,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpCircle size={18} style={{ color: scoreColor }} />
                  <Typography variant='subtitle2' sx={{ fontWeight: 600, color: scoreColor }}>
                    Question {index + 1}
                  </Typography>
                </Box>
              </Box>

              {/* Score Badge */}
              <Chip
                icon={<BarChart2 size={14} style={{ color: scoreColor }} />}
                label={`${qa.score}%`}
                sx={{
                  bgcolor: 'background.paper',
                  fontWeight: 700,
                  color: scoreColor,
                  border: '1px solid',
                  borderColor,
                }}
              />
            </Box>

            {/* Question Section */}
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      mt: 0.5,
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: 'secondary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <HelpCircle size={16} style={{ color: '#7c3aed' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='caption'
                      sx={{
                        fontWeight: 700,
                        color: 'secondary.main',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Question
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.6 }}>
                      {qa.question}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Answer Section */}
              <Paper
                variant='outlined'
                sx={{
                  ml: { xs: 0, sm: 5 },
                  mb: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <MessageSquare size={18} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='caption'
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Answer
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {qa.answer}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Justification Section */}
              {qa.justification && (
                <Paper
                  variant='outlined'
                  sx={{
                    ml: { xs: 0, sm: 5 },
                    p: 2,
                    bgcolor: headerBg,
                    borderRadius: 2,
                    borderColor,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Bot size={18} style={{ color: scoreColor, flexShrink: 0, marginTop: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='caption'
                        sx={{
                          fontWeight: 700,
                          color: scoreColor,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        AI Assessment
                      </Typography>
                      <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        {qa.justification}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default QA;
